import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import '../styles/promoteProduct.css';
import Notification from '../components/Notification';
import NetworkToaster from '../components/NetworkToaster';
import config from '../config';
import Cookies from 'js-cookie';
import { FaRocket, FaChartLine, FaUsers, FaEye, FaWallet, FaCreditCard, FaCheck, FaStar, FaShieldAlt, FaArrowUp } from 'react-icons/fa';
import { getMediaUrl } from '../utils/urlHelpers';
import { PromoteProductSkeleton } from '../components/Skeletons';

const PromoteProduct = () => {
    const { productUuid } = useParams();  // Changed from productId to productUuid
    const [promotionTypes, setPromotionTypes] = useState([]);
    const [promotionType, setPromotionType] = useState('');
    const [description, setDescription] = useState('');
    const [price, setPrice] = useState(null);
    const [userBalance, setUserBalance] = useState(0);
    const [notification, setNotification] = useState({ message: '', type: '' });
    const [toasterVisible, setToasterVisible] = useState(false);
    const [toasterMessage, setToasterMessage] = useState('');
    const [toasterType, setToasterType] = useState('error');
    const [loading, setLoading] = useState(false);
    const [productData, setProductData] = useState(null);
    const [productLoading, setProductLoading] = useState(true);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [walletLoading, setWalletLoading] = useState(false);
    const [submitStep, setSubmitStep] = useState(0);
    const [submitProgress, setSubmitProgress] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const navigate = useNavigate();

    // Helper function to show toaster
    const showToaster = useCallback((message, type = 'error') => {
        setToasterMessage(message);
        setToasterType(type);
        setToasterVisible(true);
    }, []);

    // Computed values for better UX
    const isInsufficientBalance = useMemo(() => 
        price !== null && price > userBalance, 
        [price, userBalance]
    );

    const isFormValid = useMemo(() => 
        !!promotionType && !isInsufficientBalance, 
        [promotionType, isInsufficientBalance]
    );

    const selectedPromotion = useMemo(() => 
        promotionTypes.find(promo => promo.promotion_type.toString() === promotionType),
        [promotionTypes, promotionType]
    );

    // Enhanced product image handling
    const productImage = useMemo(() => {
        if (!productData) return null;
        
        return productData.media?.[0]?.url || 
               productData.media?.[0]?.image || 
               productData.image_url ||
               productData.first_image ||
               null;
    }, [productData]);

    // Fetch wallet balance separately for better UX
    const fetchWalletBalance = useCallback(async () => {
        setWalletLoading(true);
        try {
            const token = Cookies.get('authToken');
            if (!token) return;
            
            const response = await fetch(`${config.backendUrl}/payments/wallet/`, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Token ${token}`,
                },
            });

            if (response.ok) {
                const data = await response.json();
                const balance = parseFloat(data.balance) || 0;
                setUserBalance(balance);
            }
        } catch (error) {

        } finally {
            setWalletLoading(false);
        }
    }, []);

    useEffect(() => {
        // Validate productUuid parameter
        if (!productUuid) {
            console.error('Invalid product UUID:', productUuid);
            showToaster('UUID de produit invalide. Veuillez vérifier le lien.', 'error');
            setProductLoading(false);
            return;
        }

        const fetchProductData = async () => {
            setProductLoading(true);
            try {
                const token = Cookies.get('authToken');
                const headers = {
                    'Content-Type': 'application/json',
                };
                
                // Add authorization header if token exists
                if (token) {
                    headers.Authorization = `Token ${token}`;
                }
                
                const apiUrl = `${config.backendUrl}/products/view/${productUuid}/`;  // Changed to productUuid

                const response = await fetch(apiUrl, {
                    method: 'GET',
                    headers: headers,
                });

                if (response.ok) {
                    const data = await response.json();
                    setProductData(data);

                } else {
                    const errorText = await response.text();
                    console.error('❌ Failed to fetch product details, status:', response.status, 'Response:', errorText);
                    
                    if (response.status === 404) {
                        showToaster('Produit non trouvé. Vérifiez que ce produit existe toujours.', 'error');
                    } else if (response.status === 401) {
                        showToaster('Vous devez être connecté pour accéder à cette page.', 'error');
                    } else {
                        showToaster('Impossible de récupérer les détails du produit.', 'error');
                    }
                }
            } catch (error) {
                console.error('💥 Network error while fetching product details:', error);
                showToaster('Une erreur est survenue lors de la récupération des détails du produit. Vérifiez votre connexion internet.', 'error');
            } finally {
                setProductLoading(false);
            }
        };

        fetchProductData();
    }, [productUuid]);  // Changed from productId to productUuid

    useEffect(() => {
        const fetchPromotionTypes = async () => {
            try {
                const token = Cookies.get('authToken');
                const response = await fetch(`${config.backendUrl}/products/promote/`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Token ${token}`,
                    },
                });

                if (response.ok) {
                    const data = await response.json();
                    if (Array.isArray(data.results)) {
                        setPromotionTypes(data.results);
                        const balance = parseFloat(data.user_balance) || 0;
                        setUserBalance(balance);
                    } else {
                        console.error('Unexpected response format:', data);
                        setPromotionTypes([]);
                    }
                } else {
                    console.error('Failed to fetch promotion types');
                    showToaster('Impossible de récupérer les types de promotion.', 'error');
                }
            } catch (error) {
                console.error('An error occurred while fetching promotion types:', error);
                showToaster('Une erreur est survenue lors de la récupération des types de promotion.', 'error');
            }
        };

        fetchPromotionTypes();
    }, []);

    // Separate wallet balance fetch for better UX
    useEffect(() => {
        fetchWalletBalance();
    }, [fetchWalletBalance]);

    const handlePromotionTypeChange = useCallback((e) => {
        const selectedType = e.target ? e.target.value : e;
        if (!selectedType) return;
        
        const selectedPromotion = promotionTypes.find(promo => promo.promotion_type.toString() === selectedType);
        
        if (!selectedPromotion) {

            return;
        }
        
        // Check if user can afford this promotion
        if (selectedPromotion.price > userBalance) {
            showToaster(`Solde insuffisant pour cette promotion. Il vous faut ${selectedPromotion.price} Zaires, mais vous n'avez que ${userBalance} Zaires.`, 'warning');
            return;
        }
        
        setPromotionType(selectedType);
        setDescription(selectedPromotion.description || '');
        setPrice(selectedPromotion.price || null);
        setSelectedPlan(selectedPromotion);
        
        // Add visual feedback for plan cards if they exist
        const element = document.querySelector(`[data-promotion-id="${selectedPromotion.id}"]`);
        if (element) {
            element.classList.add('selected-animation');
            setTimeout(() => {
                element.classList.remove('selected-animation');
            }, 300);
        }
    }, [promotionTypes, userBalance]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!promotionType) {
            showToaster('Veuillez sélectionner un type de promotion.', 'error');
            return;
        }

        if (price !== null && price > userBalance) {
            showToaster('Solde insuffisant pour cette promotion.', 'error');
            return;
        }

        setLoading(true);
        setSubmitting(true);
        setSubmitStep(1);
        setSubmitProgress('Validation des données...');

        try {
            setSubmitStep(2);
            setSubmitProgress('Préparation de la promotion...');

            const token = Cookies.get('authToken');
            
            setSubmitStep(3);
            setSubmitProgress('Connexion au serveur...');

            // Find the selected promotion to get its ID
            const selectedPromotion = promotionTypes.find(promo => promo.promotion_type.toString() === promotionType);
            if (!selectedPromotion) {
                throw new Error('Type de promotion invalide');
            }

            const response = await fetch(`${config.backendUrl}/products/promote/${productUuid}/${selectedPromotion.id}/`, {  // Changed to productUuid
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Token ${token}`,
                },
            });

            setSubmitStep(4);
            setSubmitProgress('Traitement de la réponse...');

            if (response.ok) {
                setSubmitStep(5);
                setSubmitProgress('Promotion réussie!');

                const productName = productData?.name || 'Votre produit';
                setNotification({ 
                    message: `${productName} a été promu avec succès!`, 
                    type: 'success',
                    header: 'Promotion Réussie'
                });
                
                // Refresh wallet balance
                await fetchWalletBalance();
                
                setTimeout(() => {
                    navigate(`/products/view/${productUuid}`);  // Changed to productUuid
                }, 2000);
            } else {
                const errorData = await response.json();
                
                if (errorData.detail && errorData.detail.includes('balance')) {
                    showToaster('Solde insuffisant pour cette promotion.', 'error');
                } else if (response.status === 401) {
                    showToaster('Vous devez être connecté pour promouvoir un produit.', 'error');
                } else if (response.status === 404) {
                    showToaster('Le produit que vous essayez de promouvoir est introuvable.', 'error');
                } else if (response.status === 500) {
                    showToaster('Erreur serveur temporaire. Votre solde n\'a pas été débité. Veuillez réessayer dans quelques instants.', 'warning');
                } else {
                    showToaster(errorData.detail || 'Une erreur s\'est produite lors de la promotion du produit.', 'error');
                }
            }
        } catch (error) {
            console.error('An error occurred during promotion:', error);
            
            let errorMessage = 'Erreur lors de la promotion du produit.';
            let errorType = 'error';
            
            if (error.message && (error.message.includes('NetworkError') || error.message.includes('Failed to fetch'))) {
                errorMessage = 'Connexion internet instable. Réessayez dans quelques instants.';
                errorType = 'warning';
            }
            
            showToaster(errorMessage, errorType);
        } finally {
            setLoading(false);
            setSubmitting(false);
            setSubmitStep(0);
            setSubmitProgress('');
        }
    };

    const handleCancel = () => {
        navigate(`/products/view/${productUuid}`);  // Changed to productUuid
    };

    return (
        <div className="promote-product-container">
            {notification.message && (
                <Notification
                    message={notification.message}
                    type={notification.type}
                    header={notification.header || ''}
                    onClose={() => setNotification({ message: '', type: '', header: '' })}
                />
            )}
            <div className="promote-header">
                <FaRocket className="header-icon" />
                <h1>Propulsez Votre Produit</h1>
                <p className="subtitle">Maximisez la visibilité de votre annonce</p>
            </div>

            {/* Product Preview Section */}
            <div className="product-preview-section">
                {productLoading ? (
                    <PromoteProductSkeleton />
                ) : productData ? (
                    <div className="product-preview-card">
                        <h2>Produit à Promouvoir</h2>
                        <div className="product-preview-content">
                            <div className="product-image-container">
                                {productImage ? (
                                    <div className="product-image-wrapper">
                                        <img 
                                            src={getMediaUrl(productImage)} 
                                            alt={productData.name} 
                                            className="product-preview-image"
                                            onError={(e) => {
                                                console.error('Image failed to load:', e.target.src);
                                                e.target.style.display = 'none';
                                            }}
                                        />
                                        <div className="image-overlay">
                                            <div className="product-badge">
                                                <FaRocket className="badge-icon" />
                                                <span>À promouvoir</span>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="no-image-placeholder">
                                        <FaRocket className="placeholder-icon" />
                                        <span>Pas d'image</span>
                                    </div>
                                )}
                            </div>
                            <div className="product-preview-details">
                                <h3>{productData.name}</h3>
                                <p className="product-preview-price">
                                    {productData.currency} {productData.price ? parseFloat(productData.price).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : 'Prix non disponible'}
                                </p>
                                <div className="product-meta">
                                    <div className="meta-item">
                                        <span className="meta-label">Catégorie:</span>
                                        <span className="meta-value">{productData.category_name || 'Non spécifiée'}</span>
                                    </div>
                                    <div className="meta-item">
                                        <span className="meta-label">État:</span>
                                        <span className="meta-value">{productData.condition || 'Non spécifié'}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="product-preview-error">
                        <h2>Produit à Promouvoir</h2>
                        <div className="product-preview-fallback">
                            <div className="fallback-icon">
                                <FaRocket size={48} />
                            </div>
                            <p>Les détails du produit ne peuvent pas être chargés, mais vous pouvez toujours le promouvoir.</p>
                            <p className="product-id-info">UUID du produit: {productUuid}</p>  {/* Changed to productUuid */}
                        </div>
                    </div>
                )}
            </div>

            {/* Wallet Balance Section */}
            <div className="wallet-balance-section">
                <div className="wallet-card">
                    <div className="wallet-header">
                        <FaWallet className="wallet-icon" />
                        <div className="wallet-info">
                            <h3>Solde Disponible</h3>
                            {walletLoading ? (
                                <div className="wallet-loading">
                                    <div className="small-spinner"></div>
                                    <span>Chargement...</span>
                                </div>
                            ) : (
                                <p className={`wallet-balance ${isInsufficientBalance ? 'insufficient' : ''}`}>
                                    {(typeof userBalance === 'number' ? userBalance : parseFloat(userBalance) || 0).toFixed(2)} <span className="currency">Zaires</span>
                                </p>
                            )}
                        </div>
                    </div>
                    {isInsufficientBalance && selectedPromotion && (
                        <div className="insufficient-balance-warning">
                            <div className="warning-content">
                                <span className="warning-icon">⚠️</span>
                                <span>Solde insuffisant pour cette promotion</span>
                            </div>
                            <button 
                                className="add-credits-btn"
                                onClick={() => navigate('/wallet/topup')}
                            >
                                <FaCreditCard />
                                Ajouter des crédits
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Show warning if product is already promoted */}
            {productData && productData.is_promotion_active && (
                <div className="already-promoted-container">
                    <div className="already-promoted-header">
                        <FaCheck className="success-icon" />
                        <h2>Produit Déjà Promu</h2>
                    </div>
                    <p className="already-promoted-message">
                        Ce produit bénéficie déjà d'une promotion active. Vous ne pouvez pas promouvoir un produit qui est déjà sponsorisé.
                    </p>
                    {productData.promoted_end_date && (
                        <div className="promotion-end-date">
                            <span className="date-icon">📅</span>
                            <span>
                                Promotion active jusqu'au {new Date(productData.promoted_end_date).toLocaleDateString('fr-FR', {
                                    day: '2-digit',
                                    month: 'long',
                                    year: 'numeric'
                                })}
                            </span>
                        </div>
                    )}
                    <button 
                        className="back-to-product-btn"
                        onClick={handleCancel}
                    >
                        <span>←</span>
                        Retour au produit
                    </button>
                </div>
            )}

            {/* Only show promotion options if product is NOT already promoted */}
            {!productData?.is_promotion_active && (
                <>
                    <div className="benefits-section">
                        <h2>Pourquoi promouvoir votre produit ?</h2>
                <div className="benefits-carousel">
                    <div className="benefit-card">
                        <div className="benefit-icon-container">
                            <FaEye className="benefit-icon" />
                        </div>
                        <h3>Visibilité Premium</h3>
                        <p>Votre produit apparaît en priorité dans les résultats de recherche et les catégories.</p>
                        <div className="benefit-features">
                            <div className="feature-item">
                                <FaArrowUp className="feature-icon" />
                                <span>Placement en avant</span>
                            </div>
                        </div>
                    </div>
                    <div className="benefit-card">
                        <div className="benefit-icon-container">
                            <FaChartLine className="benefit-icon" />
                        </div>
                        <h3>Ventes Accélérées</h3>
                        <p>Les produits promus se vendent en moyenne 3 fois plus rapidement que les autres.</p>
                        <div className="benefit-features">
                            <div className="feature-item">
                                <FaCheck className="feature-icon" />
                                <span>Résultats prouvés</span>
                            </div>
                        </div>
                    </div>
                    <div className="benefit-card">
                        <div className="benefit-icon-container">
                            <FaUsers className="benefit-icon" />
                        </div>
                        <h3>Plus d'Interactions</h3>
                        <p>Augmentez significativement vos chances d'être contacté par des acheteurs intéressés.</p>
                        <div className="benefit-features">
                            <div className="feature-item">
                                <FaStar className="feature-icon" />
                                <span>Badge premium</span>
                            </div>
                        </div>
                    </div>
                    <div className="benefit-card">
                        <div className="benefit-icon-container">
                            <FaShieldAlt className="benefit-icon" />
                        </div>
                        <h3>Statut Premium</h3>
                        <p>Différenciez-vous avec un badge premium qui inspire confiance aux acheteurs.</p>
                        <div className="benefit-features">
                            <div className="feature-item">
                                <FaCheck className="feature-icon" />
                                <span>Visibilité prioritaire</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Enhanced Promotion Plans */}
            <div className="promotion-plans-section">
                <h2>Choisissez votre plan</h2>
                <p className="section-subtitle">Sélectionnez la durée de promotion qui convient le mieux à vos besoins</p>
                
                <div className="promotion-plans-grid">
                    {promotionTypes.map(promo => (
                        <div
                            key={promo.id}
                            data-promotion-id={promo.id}
                            className={`promotion-plan ${promotionType === promo.promotion_type.toString() ? 'selected' : ''} ${promo.price > userBalance ? 'disabled' : ''} ${submitting ? 'disabled' : ''}`}
                            onClick={() => !submitting && handlePromotionTypeChange(promo.promotion_type.toString())}
                            style={{ cursor: submitting ? 'not-allowed' : 'pointer' }}
                        >
                            <div className="plan-header">
                                <div className="plan-duration">
                                    <span className="duration-number">{promo.promotion_type}</span>
                                    <span className="duration-label">jours</span>
                                </div>
                                <div className="plan-price">
                                    <span className="price-amount">{promo.price ? promo.price.toFixed(2) : '0.00'}</span>
                                    <span className="price-currency">Zaires</span>
                                </div>
                                {promotionType === promo.promotion_type.toString() && (
                                    <div className="selected-indicator">
                                        <FaCheck />
                                    </div>
                                )}
                            </div>
                            
                            <div className="plan-content">
                                <p className="plan-description">{promo.description}</p>
                                <div className="plan-features">
                                    <div className="feature-item">
                                        <FaArrowUp className="feature-icon" />
                                        <span>Visibilité prioritaire</span>
                                    </div>
                                    <div className="feature-item">
                                        <FaShieldAlt className="feature-icon" />
                                        <span>Badge premium</span>
                                    </div>
                                    <div className="feature-item">
                                        <FaStar className="feature-icon" />
                                        <span>Placement en avant</span>
                                    </div>
                                </div>
                            </div>
                            
                            {promo.price > userBalance && (
                                <div className="insufficient-balance-overlay">
                                    <span>Solde insuffisant</span>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {/* Selected Plan Summary */}
            {selectedPromotion ? (
                <div className="plan-summary-section">
                    <h2>Récapitulatif</h2>
                    <div className="summary-content">
                        <div className="summary-row">
                            <span>Plan sélectionné:</span>
                            <span className="summary-value">{selectedPromotion.promotion_type} jours</span>
                        </div>
                        <div className="summary-row">
                            <span>Prix:</span>
                            <span className="summary-price">{selectedPromotion.price?.toFixed(2) || '0.00'} Zaires</span>
                        </div>
                        <div className="summary-description">
                            <p>{description || selectedPromotion.description || 'Aucune description disponible'}</p>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="no-plan-selected">
                    <div className="no-plan-content">
                        <FaRocket className="no-plan-icon" />
                        <p>Sélectionnez un plan de promotion pour voir le récapitulatif</p>
                    </div>
                </div>
            )}

            {/* Action Section */}
            <div className="action-section">
                <form onSubmit={handleSubmit} className="promote-form">
                    <div className="form-actions">
                        <button 
                            type="button" 
                            className="cancel-button" 
                            onClick={handleCancel} 
                            disabled={loading || submitting}
                        >
                            Annuler
                        </button>
                        <button 
                            type="submit" 
                            className={`promote-product-submit-button ${!isFormValid || loading || submitting ? 'disabled' : ''}`}
                            disabled={!isFormValid || loading || submitting}
                        >
                            {submitting ? (
                                <div className="dots-loader">
                                    <span className="dot"></span>
                                    <span className="dot"></span>
                                    <span className="dot"></span>
                                </div>
                            ) : (
                                <>
                                    <FaRocket className="button-icon" />
                                    Promouvoir Maintenant
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
                </>
            )}

            {/* Overlay to disable page while submitting */}
            {submitting && <div className="submitting-overlay"></div>}

            {notification.message && (
                <Notification
                    message={notification.message}
                    type={notification.type}
                    header={notification.header || ''}
                    onClose={() => setNotification({ message: '', type: '', header: '' })}
                />
            )}
            
            <NetworkToaster
                message={toasterMessage}
                visible={toasterVisible}
                onClose={() => setToasterVisible(false)}
                type={toasterType}
            />
        </div>
    );
};

export default PromoteProduct;